import { isAxiosError } from 'axios';
import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import { type ChangePasswordData, changeUserPassword } from '@core/api/user';
import { RhfTextField } from '@core/components/forms/fields';
import { useAuth } from '@core/contexts/AuthContext';
import FormDialog from './FormDialog';

export interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

type ChangePasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
}) => {
  const { user } = useAuth();

  const defaultValues = useMemo<ChangePasswordFormValues>(
    () => ({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    }),
    []
  );

  const onSubmit = async (data: ChangePasswordFormValues) => {
    if (!user) {
      return;
    }
    const payload: ChangePasswordData = {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword,
    };
    try {
      await changeUserPassword(user.id, payload);
      toast.success('Password changed successfully');
    } catch (error) {
      if (isAxiosError(error) && error.response?.data?.errors) {
        const errors = error.response.data.errors as Record<string, string>;
        const message = Object.values(errors)[0];
        toast.error(message ?? 'Failed to change password');
      } else {
        toast.error('Failed to change password');
      }
      throw error;
    }
  };

  if (!user) {
    return null;
  }

  return (
    <FormDialog<ChangePasswordFormValues>
      open={open}
      onClose={onClose}
      title="Change password"
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText="Change password"
      cancelText="Cancel"
    >
      {({ watch }) => (
        <>
          <RhfTextField<ChangePasswordFormValues>
            name="currentPassword"
            label="Current password"
            required
            disableClipboard
            textFieldProps={{
              type: 'password',
              autoComplete: 'current-password',
            }}
          />
          <RhfTextField<ChangePasswordFormValues>
            name="newPassword"
            label="New password"
            required
            disableClipboard
            rules={{
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters',
              },
            }}
            textFieldProps={{
              type: 'password',
              autoComplete: 'new-password',
            }}
          />
          <RhfTextField<ChangePasswordFormValues>
            name="confirmPassword"
            label="Confirm new password"
            required
            disableClipboard
            rules={{
              validate: (value) =>
                !value ||
                value === watch('newPassword') ||
                'Passwords must match',
            }}
            textFieldProps={{
              type: 'password',
              autoComplete: 'new-password',
            }}
          />
        </>
      )}
    </FormDialog>
  );
};

export default ChangePasswordDialog;
